import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

import {
  Box, Columns, DashboardModule, Heading, LoadingModule, Text, SidebarModal,
} from '@domain-group/suit-theme'

import { useFilters } from '@domain-group/react-hooks'

import useCanvaAutofillFields from '@hooks/useCanvaAutofillFields'
import useCurrentUser from '@hooks/useCurrentUser'
import useCustomShortcode from '@hooks/useCustomShortcode'
import useCustomShortcodes from '@hooks/useCustomShortcodes'

import type { CustomShortCodeModel } from '@domain-group/realhub-backend-api-types'

import PageFilterModule from '@components/PageFilterModule'

import Header from '../../../Header'
import FieldMappingRow from './FieldMappingRow'

const OWNER_TYPE = 'Brand'

const buildPageFilterFields = () => [
  {
    component: 'DynamicInput',
    componentProps: {
      inputComponent: 'input',
      style: { marginBottom: 16 },
    },
    key: 'string',
    label: 'Search field by name or shortcode',
  },
]

const PageContent = () => {
  const { brandId, isBrandUser, loading: loadingCurrentUser } = useCurrentUser()

  const { fields, loading: loadingFields } = useCanvaAutofillFields()

  const { filteredCustomShortcodes, loading: loadingShortcodes } = useCustomShortcodes({
    filters: { owner_id: brandId, owner_type: OWNER_TYPE },
    performHttpRequests: !!brandId,
  })

  const { callbacks } = useCustomShortcode()

  const shortcodesByKey = useMemo(() => (
    filteredCustomShortcodes.reduce((acc, customShortcode) => {
      acc[customShortcode.key] = customShortcode
      return acc
    }, {} as Record<string, CustomShortCodeModel>)
  ), [filteredCustomShortcodes])

  const groupedFields = useMemo(() => {
    const groups: { group: string, fields: typeof fields }[] = []

    fields.forEach((field) => {
      const existingGroup = groups.find(entry => entry.group === field.group)

      if (existingGroup){
        existingGroup.fields.push(field)
      } else {
        groups.push({ group: field.group, fields: [field] })
      }
    })

    return groups
  }, [fields])

  const {
    callbacks: { resetFilters, updateFilters },
    defaultFilters,
    pageFilters,
  } = useFilters({})

  const { string: searchQuery = '' } = pageFilters as { string?: string }

  const filteredGroupedFields = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    if (!query) return groupedFields

    return groupedFields
      .map(({ group, fields: groupFields }) => ({
        group,
        fields: groupFields.filter(field => (
          field.label.toLowerCase().includes(query) || field.key.toLowerCase().includes(query)
        )),
      }))
      .filter(entry => entry.fields.length > 0)
  }, [groupedFields, searchQuery])

  const loading = loadingCurrentUser || loadingFields || loadingShortcodes

  return (
    <>
      <Helmet>
        <title>Canva Templates | Template Manager</title>
      </Helmet>

      <Header activeTabBarItemKey='field_mapping' />

      <Box flexDirection="column" paddingX="large" paddingTop={[112, 121]}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <SidebarModal.NoticeBox boxProps={{ variant: 'white' }}>
              <SidebarModal.NoticeBox.Title>What is Field Mapping?</SidebarModal.NoticeBox.Title>
              <SidebarModal.NoticeBox.Paragraph>
                Realhub uses default shortcodes (e.g. {'{bedrooms}'}) to populate property data in Canva templates. If your template uses different shortcodes (e.g. [beds] or {'<bedrooms>'}), enter them in your shortcode column and we'll map them automatically during autofill. Leave the field blank to use the default Realhub shortcode.
              </SidebarModal.NoticeBox.Paragraph>
            </SidebarModal.NoticeBox>
                {!isBrandUser && !loadingCurrentUser && (
                  <Text color="bodyFontLightColor" fontSize="small">
                    Field mapping is only available to brand users
                  </Text>
                )}

                {brandId && (
                  <>
                    {loading && <LoadingModule loading />}

                    {!loading && filteredGroupedFields.length === 0 && (
                      <Text color="bodyFontLightColor" fontSize="small">
                        No fields match your filters
                      </Text>
                    )}

                    {!loading && filteredGroupedFields.map(({ group, fields: groupFields }) => (
                      <DashboardModule title={group}>
                        <Box flexDirection="column" padding="large">
                          <Box alignItems="center" borderBottom="1px solid" borderColor="lineColor" paddingBottom="medium">
                            <Box width="40%">
                              <Text color="bodyFontLightColor" fontSize="xsmall" fontWeight="bold">Field</Text>
                            </Box>

                            <Box width="30%">
                              <Text color="bodyFontLightColor" fontSize="xsmall" fontWeight="bold">Realhub Shortcode</Text>
                            </Box>

                            <Box width="30%">
                              <Text color="bodyFontLightColor" fontSize="xsmall" fontWeight="bold">Canva Shortcode (Optional)</Text>
                            </Box>
                          </Box>

                          <Box flexDirection="column" key={group}>
                            {groupFields.map(field => (
                              <FieldMappingRow
                                brandId={brandId}
                                callbacks={callbacks}
                                customShortcode={shortcodesByKey[field.key]}
                                field={field}
                                key={`${brandId}-${field.key}`}
                              />
                            ))}
                          </Box>
                        </Box>
                      </DashboardModule>
                    ))}
                  </>
                )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
